import { defineStore } from 'pinia'
import type { Article, Category } from '@/types'

/**
 * Blog servi EN DIRECT : ses pages ne sont plus pre-rendues au build. Le serveur live
 * (server/live-server.mjs) les rend a la demande depuis l'API et depose ici les donnees
 * de la page (serialisees avec l'etat initial). Une navigation interne vers le blog, la
 * pagination et le filtre par categorie interrogent les routes JSON /__live/*.
 */

export interface BlogList {
  items: Article[]
  total: number
  page: number
  perPage: number
  categoryId: number | null
}

export interface BlogPayload {
  kind: 'article' | 'category' | 'blog'
  path: string
  article?: Article
  category?: Category
  related?: Article[]
  categories: Category[]
  list?: BlogList
}

export const useBlogStore = defineStore('blog', {
  state: () => ({
    payload: null as BlogPayload | null,
    // Chemin deja verifie absent du blog (rendu 404 par le serveur) : evite une
    // nouvelle requete a l'hydratation.
    missPath: null as string | null,
  }),
})

/** Donnees live d'une page du blog, ou null si le chemin n'en est pas une. */
export async function fetchBlogPage(path: string): Promise<BlogPayload | null> {
  const res = await fetch(`/__live/page?path=${encodeURIComponent(path)}`, {
    headers: { accept: 'application/json' },
  })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as BlogPayload
}

/** Une page de la liste d'articles (toutes categories ou une seule). */
export async function fetchBlogList(page: number, categoryId: number | null): Promise<BlogList> {
  const q = new URLSearchParams({ page: String(page) })
  if (categoryId != null) q.set('category', String(categoryId))
  const res = await fetch(`/__live/articles?${q}`, { headers: { accept: 'application/json' } })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as BlogList
}

/** Categorie principale d'un article, parmi les categories live. */
export function primaryCategoryIn(article: Article, categories: Category[]): Category | undefined {
  const byId = new Map(categories.map((c) => [c.wp_id, c]))
  if (article.primary_category_id != null && byId.has(article.primary_category_id)) {
    return byId.get(article.primary_category_id)
  }
  const id = (article.category_ids || []).find((c) => byId.has(c))
  return id != null ? byId.get(id) : undefined
}

/** Categories non vides, triees par nom (puces de filtre : jamais de choix vide). */
export function nonEmptyIn(categories: Category[]): Category[] {
  return categories
    .filter((c) => c.count > 0 && c.slug !== 'a-classer')
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
}
